import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../components/common/Modal";
import { Input } from "../../../components/common/Input";
import type { ClientUpsertPayload } from "../types";

type FieldErrors = Partial<Record<keyof ClientUpsertPayload, string>> & {
  [key: string]: string | undefined;
};

type ClientModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pending: boolean;
  fieldErrors?: FieldErrors;
  errorMessage?: string | null;
  mode?: "create" | "edit";
  initialValues?: Partial<ClientUpsertPayload>;
  onSubmit: (payload: ClientUpsertPayload) => void;
};

const EMPTY_VALUES: ClientUpsertPayload = {
  fullname: "",
  phone: "",
  telegram: "",
  whatsapp: "",
  instagram: "",
};

export const AddClientModal: React.FC<ClientModalProps> = ({
  isOpen,
  onClose,
  pending,
  fieldErrors,
  errorMessage,
  mode = "create",
  initialValues,
  onSubmit,
}) => {
  const [values, setValues] = useState<ClientUpsertPayload>(EMPTY_VALUES);
  const [localErrors, setLocalErrors] = useState<FieldErrors>({});
  const initialFullname = initialValues?.fullname;
  const initialPhone = initialValues?.phone;
  const initialTelegram = initialValues?.telegram;
  const initialWhatsapp = initialValues?.whatsapp;
  const initialInstagram = initialValues?.instagram;

  useEffect(() => {
    if (!isOpen) return;

    setValues({
      ...EMPTY_VALUES,
      fullname: initialFullname ?? "",
      phone: initialPhone ?? "",
      telegram: initialTelegram ?? "",
      whatsapp: initialWhatsapp ?? "",
      instagram: initialInstagram ?? "",
    });
    setLocalErrors({});
  }, [
    isOpen,
    initialFullname,
    initialPhone,
    initialTelegram,
    initialWhatsapp,
    initialInstagram,
  ]);

  const visibleErrors = useMemo(
    () => ({ ...fieldErrors, ...localErrors }),
    [fieldErrors, localErrors],
  );

  const setField = (field: keyof ClientUpsertPayload, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setLocalErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = () => {
    const nextErrors: FieldErrors = {};

    if (!values.fullname.trim()) {
      nextErrors.fullname = "Укажите ФИО клиента.";
    }

    if (!values.phone.trim()) {
      nextErrors.phone = "Укажите телефон клиента.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setLocalErrors(nextErrors);
      return;
    }

    onSubmit({
      fullname: values.fullname.trim(),
      phone: values.phone.trim(),
      telegram:
        mode === "edit"
          ? values.telegram?.trim() || null
          : values.telegram?.trim() || undefined,
      whatsapp:
        mode === "edit"
          ? values.whatsapp?.trim() || null
          : values.whatsapp?.trim() || undefined,
      instagram:
        mode === "edit"
          ? values.instagram?.trim() || null
          : values.instagram?.trim() || undefined,
    });
  };

  const isEdit = mode === "edit";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Редактировать клиента" : "Новый клиент"}
      text={
        isEdit
          ? "Измените контактные данные клиента."
          : "Заполните данные клиента, чтобы добавить его в базу."
      }
      actionButtonText={isEdit ? "Сохранить" : "Добавить"}
      actionButtonCallback={handleSubmit}
      actionButtonPending={pending}
      cancelButtonText="Отмена"
      className=""
    >
      <div className="flex flex-col gap-3 mt-2 mb-5">
        {errorMessage && (
          <div
            className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        <Input
          label="ФИО *"
          name="fullname"
          type="text"
          placeholder="Иван Иванович Иванов"
          value={values.fullname}
          onChange={(value: string) => setField("fullname", value)}
          onBlur={undefined}
          register={{}}
          error={Boolean(visibleErrors.fullname)}
          errorMessage={visibleErrors.fullname}
          autoComplete="name"
          inputMode={undefined}
          variant={undefined}
        />

        <Input
          label="Телефон *"
          name="phone"
          type="tel"
          inputMode="tel"
          placeholder="+7 (999) 111-22-33"
          value={values.phone}
          onChange={(value: string) => setField("phone", value)}
          onBlur={undefined}
          register={{}}
          error={Boolean(visibleErrors.phone)}
          errorMessage={visibleErrors.phone}
          autoComplete="tel"
          variant={undefined}
        />

        <Input
          label="Telegram"
          name="telegram"
          type="text"
          placeholder="@username"
          value={values.telegram ?? ""}
          onChange={(value: string) => setField("telegram", value)}
          onBlur={undefined}
          register={{}}
          error={Boolean(visibleErrors.telegram)}
          errorMessage={visibleErrors.telegram}
          inputMode={undefined}
          variant={undefined}
        />

        <Input
          label="WhatsApp"
          name="whatsapp"
          type="tel"
          inputMode="tel"
          placeholder="+7 (999) 111-22-33"
          value={values.whatsapp ?? ""}
          onChange={(value: string) => setField("whatsapp", value)}
          onBlur={undefined}
          register={{}}
          error={Boolean(visibleErrors.whatsapp)}
          errorMessage={visibleErrors.whatsapp}
          variant={undefined}
        />

        <Input
          label="Instagram"
          name="instagram"
          type="text"
          placeholder="username"
          value={values.instagram ?? ""}
          onChange={(value: string) => setField("instagram", value)}
          onBlur={undefined}
          register={{}}
          error={Boolean(visibleErrors.instagram)}
          errorMessage={visibleErrors.instagram}
          inputMode={undefined}
          variant={undefined}
        />
      </div>
    </Modal>
  );
};
